export const FINDING_CLUSTER_CAUSES = [
    "connector-scope",
    "stale-source",
    "policy-requirement",
    "control-owner",
];
function hoursBetween(from, to) {
    return (Date.parse(to) - Date.parse(from)) / 3600000;
}
function isStale(finding, context) {
    if (finding.collectedAt == null)
        return true;
    const age = hoursBetween(finding.collectedAt, context.now);
    return !Number.isFinite(age) || age > context.stalenessThresholdHours;
}
function causeFor(finding, context) {
    if (finding.connectorId != null && !finding.connectorScopeSatisfied) {
        return { cause: "connector-scope", key: finding.connectorId };
    }
    if (isStale(finding, context)) {
        return { cause: "stale-source", key: finding.connectorId ?? "no-connector" };
    }
    if (finding.policyRequirementId != null) {
        return { cause: "policy-requirement", key: finding.policyRequirementId };
    }
    return { cause: "control-owner", key: finding.ownerActorId ?? "unassigned" };
}
function statementFor(cause, key, count) {
    const findings = count === 1 ? "1 finding" : `${count} findings`;
    switch (cause) {
        case "connector-scope":
            return {
                causeStatement: `${findings} are unresolved because the connector ${key} is not granted the scope these controls need, so no evidence can be collected for them at all.`,
                sharedFix: `Re-authorize connector ${key} with the missing scope, then re-run collection.`,
            };
        case "stale-source":
            return {
                causeStatement: `${findings} rest on evidence from ${key} that is older than the freshness window, so the control state is unknown rather than failing.`,
                sharedFix: `Re-collect from ${key} and re-evaluate; no control change is implied until fresh evidence arrives.`,
            };
        case "policy-requirement":
            return {
                causeStatement: `${findings} fail the same policy requirement ${key}, so they share one root cause rather than being separate defects.`,
                sharedFix: `Resolve requirement ${key} once — either remediate the control or record an exception against it.`,
            };
        case "control-owner":
            return {
                causeStatement: `${findings} are owned by ${key}, who is the single person able to act on all of them.`,
                sharedFix: `Route the group to ${key} for a single review pass.`,
            };
    }
}
export function clusterFindings(findings, context) {
    const groups = new Map();
    for (const finding of findings) {
        const { cause, key } = causeFor(finding, context);
        const id = `${cause}:${key}`;
        const existing = groups.get(id);
        if (existing)
            existing.findingIds.push(finding.id);
        else
            groups.set(id, { cause, key, findingIds: [finding.id] });
    }
    return [...groups.entries()]
        .map(([id, group]) => ({
        id,
        cause: group.cause,
        ...statementFor(group.cause, group.key, group.findingIds.length),
        findingIds: [...group.findingIds].sort(),
    }))
        .sort((left, right) => left.id.localeCompare(right.id));
}
